import React, { useEffect, useMemo, useState } from 'react';
import { getStoredToken } from '../services/authSession';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { RefreshCw, Download, Info, Calculator, FileSpreadsheet, TrendingUp, ChevronDown, ChevronUp, List } from 'lucide-react';
import API from '../services/apiBase';
import GLSectionDisplay from './GLSectionDisplay';
import ScheduleEDetail from './ScheduleEDetail';
import { useClient } from '../context/ClientContext';
import { sectionType, sectionsOfType } from '../lib/sectionType';

const SectionTable = ({ section, sectionKey = '' }) => {
  if (!section) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
        <Calculator style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.3 }} />
        <p>No data available for this section</p>
        <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Run calculations to see results</p>
      </div>
    );
  }

  const formatValue = (val, label = '') => {
    if (val === null || val === undefined || val === '') return '—';
    if (typeof val === 'number') {
      const labelLower = (label || '').toLowerCase();
      // Check if it's a rate or percentage
      if (labelLower.includes('rate') || labelLower.includes('% of total')) {
        return `${(val * 100).toFixed(4)}%`;
      }
      // Entry counts and other integer fields — display as plain numbers
      if (labelLower.includes('entry count') || labelLower.includes('count')) {
        return val.toLocaleString();
      }
      // Percentage columns (value between 0 and 1, not an integer)
      if (labelLower.includes('%')) {
        return `${(val * 100).toFixed(2)}%`;
      }
      // Check if it's a percentage (value between 0 and 1)
      if (Math.abs(val) <= 1 && val !== 0 && !Number.isInteger(val)) {
        return `${(val * 100).toFixed(2)}%`;
      }
      // Currency formatting
      return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return String(val);
  };

  const renderTable = (rows, title = null, options = {}) => {
    if (!rows || rows.length === 0) return null;
    // integerLabels: Set of label/column names to format as plain integers (no $ sign)
    const integerLabels = options.integerLabels || new Set();

    // Use 'label' and 'value' fields if present (new format)
    // Fall back to first object keys if not
    const hasLabelValue = rows[0] && ('label' in rows[0] || 'name' in rows[0]);

    if (hasLabelValue) {
      // Simple label-value format (Fringe, Material & Handling, OH sections, G&A)
      return (
        <div style={{ marginBottom: '2rem' }}>
          {title && (
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {title}
            </h4>
          )}
          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
            <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
              <tbody>
                {rows.map((row, i) => {
                  const label = row.label || row.name || '';
                  const value = row.value !== undefined ? row.value : row.amount;
                  const isTotal = label.toLowerCase().includes('total');
                  const rate = row.rate; // Check if there's a rate percentage
                  const useInteger = integerLabels.has(label);

                  return (
                    <tr key={i} style={{
                      background: isTotal ? '#FFF7ED' : i % 2 === 0 ? 'transparent' : '#FAFAFA'
                    }}>
                      <td style={{
                        padding: '0.875rem 1rem',
                        textAlign: 'left',
                        fontWeight: isTotal ? '700' : '500',
                        color: 'var(--gray-700)',
                        width: '40%'
                      }}>
                        {label}
                      </td>
                      <td style={{
                        padding: '0.875rem 1rem',
                        textAlign: 'right',
                        fontFamily: 'Inter, monospace',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: isTotal ? '700' : 'normal',
                        color: 'var(--gray-900)',
                        width: '60%'
                      }}>
                        {useInteger && typeof value === 'number'
                          ? Math.round(value).toLocaleString()
                          : formatValue(value, label)}
                        {!useInteger && rate !== undefined && rate > 0 && (
                          <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: 'var(--success-green)', fontWeight: '600' }}>
                            ({(rate * 100).toFixed(3)}%)
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Table format for allocation rows
    const columns = Object.keys(rows[0]);
    return (
      <div style={{ marginBottom: '2rem' }}>
        {title && (
          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {title}
          </h4>
        )}
        <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
          <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th key={col} style={{
                    padding: '1rem',
                    textAlign: idx === 0 || col === 'label' || col.toLowerCase().includes('description') || col.toLowerCase().includes('code') ? 'left' : 'right',
                    whiteSpace: 'nowrap'
                  }}>
                    {col.replace(/_/g, ' ').toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isTotal = Object.values(row).some(v => typeof v === 'string' && v.toLowerCase().includes('total'));
                return (
                  <tr key={i} style={{ background: isTotal ? '#FFF7ED' : 'transparent', fontWeight: isTotal ? '700' : 'normal' }}>
                    {columns.map((col, idx) => (
                      <td key={col} style={{
                        padding: '0.875rem 1rem',
                        textAlign: idx === 0 || col === 'label' || col.toLowerCase().includes('description') || col.toLowerCase().includes('code') ? 'left' : 'right',
                        fontFamily: col.toLowerCase().includes('amount') || col.toLowerCase().includes('total') || col.toLowerCase().includes('rate') || col.toLowerCase().includes('%') || col === 'value' ? 'Inter, monospace' : 'inherit',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: isTotal ? '700' : 'normal',
                      }}>
                        {integerLabels.has(col) && typeof row[col] === 'number'
                          ? Math.round(row[col]).toLocaleString()
                          : formatValue(row[col], col)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Handle different data structures
  
  // Special handling for new Schedule E Bases format
  if (section.format === 'schedule_e_bases') {
    const summary = section.summary || {};
    const fringeBases = section.fringe_bases || {};
    const divisionOhBases = section.division_oh_bases || {};
    const gaComponents = section.ga_base_components || {};
    
    // Phase 7: derive a per-fringe rate card per row in
    // ``fringeBases.rows`` instead of hardcoded "Fringe 1 Rate" /
    // "Fringe 2 Rate" slots reading positional ``summary.fringe_100_rate``
    // / ``summary.fringe_101_rate``. Wizard clients with arbitrary
    // fringe section IDs and any number of fringe pools get one card
    // per pool, labeled with the user's onboarding name. Legacy
    // reference-client runs still get two cards labeled "Fringe 1
    // Rate" / "Fringe 2 Rate" because each ``fringeBases.rows`` entry
    // already carries those legacy fallback labels (see
    // ``_LEGACY_LABEL_FALLBACKS`` in the executor).
    const fringeRateCards = (fringeBases.rows || []).map((row, idx) => {
      // Row labels come from the executor as "<Name> Base" — strip the
      // " Base" suffix for the rate-card heading so it reads as
      // "Fringe 1 Rate" / "Fringe Pool 100 Rate" etc.
      const baseLabel = (row.label || `Fringe ${idx + 1}`)
        .replace(/\s+Base\s*$/i, '')
        .trim() || `Fringe ${idx + 1}`;
      return {
        key: row.key || `fringe_${idx}`,
        title: `${baseLabel} Rate`,
        rate: row.rate,
      };
    });

    return (
      <div>
        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {fringeRateCards.map((card) => (
            <div key={card.key} className="pro-stat-card">
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                {card.title}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-green)' }}>
                {formatValue(card.rate, 'rate')}
              </div>
            </div>
          ))}
          <div className="pro-stat-card">
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Total Division Bases
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-blue)' }}>
              {formatValue(summary.total_division_bases)}
            </div>
          </div>
          <div className="pro-stat-card">
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              G&A Rate
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--accent-orange)' }}>
              {formatValue(summary.ga_rate, 'rate')}
            </div>
          </div>
        </div>
        
        {/* Fringe Bases Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#E2EFDA', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
            {fringeBases.title || 'Fringe Benefit Bases'}
          </h4>
          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
            <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
              <thead>
                <tr style={{ background: '#1a1a1a' }}>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Description</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Base</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Pool</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {(fringeBases.rows || []).map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#FAFAFA' }}>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '500' }}>{row.label}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                      {formatValue(row.value)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                      {formatValue(row.pool)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', color: 'var(--success-green)' }}>
                      {formatValue(row.rate, 'rate')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Division OH Bases Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#DDEBF7', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
            {divisionOhBases.title || 'Division OH Bases'}
          </h4>
          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
            <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
              <thead>
                <tr style={{ background: '#1a1a1a' }}>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Division</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Labor Base</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Fringe Amount</th>
                  {/* Phase 7: only render the secondary fringe column
                      when at least one row has a non-null
                      ``fringe_101_amount``. Wizard clients with a
                      single fringe pool drop this column entirely
                      instead of showing a column of dashes. */}
                  {(divisionOhBases.rows || []).some(r => r && r.fringe_101_amount != null) && (
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Fringe 2</th>
                  )}
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Total Base</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>OH Rate</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Total Pool</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const hasSecondaryFringe = (divisionOhBases.rows || []).some(r => r && r.fringe_101_amount != null);
                  return (divisionOhBases.rows || []).map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#FAFAFA' }}>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '500' }}>{row.label}</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                        {formatValue(row.labor_base)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                        {formatValue(row.fringe_amount)}
                      </td>
                      {hasSecondaryFringe && (
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                          {row.fringe_101_amount != null ? formatValue(row.fringe_101_amount) : '—'}
                        </td>
                      )}
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', fontWeight: '600' }}>
                        {formatValue(row.total_base)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', color: 'var(--success-green)' }}>
                        {formatValue(row.oh_rate, 'rate')}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                        {formatValue(row.total_pool)}
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* G&A Base Components Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#FCE4D6', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
            G&A Base Components
          </h4>
          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
            <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
              <thead>
                <tr style={{ background: '#1a1a1a' }}>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Component</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'transparent' }}>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Direct Labor (Account 500)</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(gaComponents.direct_labor)}
                  </td>
                </tr>
                <tr style={{ background: '#FAFAFA' }}>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Fringe on Direct Labor</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(gaComponents.fringe_on_labor)}
                  </td>
                </tr>
                <tr style={{ background: 'transparent' }}>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Division OH Total</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(gaComponents.division_oh_total)}
                  </td>
                </tr>
                <tr style={{ background: '#FAFAFA' }}>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Travel (Account 510)</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(gaComponents.travel_510)}
                  </td>
                </tr>
                <tr style={{ background: 'transparent' }}>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Other Direct Costs (Account 520)</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(gaComponents.odc_520)}
                  </td>
                </tr>
                <tr style={{ background: '#FAFAFA' }}>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Material & Handling</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(gaComponents.material_handling)}
                  </td>
                </tr>
                {/* Total Row */}
                <tr style={{ background: '#FFF7ED', fontWeight: '700' }}>
                  <td style={{ padding: '0.875rem 1rem', textAlign: 'left' }}>G&A Total Base</td>
                  <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(gaComponents.total_base)}
                  </td>
                </tr>
                {/* Rate Row */}
                <tr style={{ background: '#E8F5E9', fontWeight: '600' }}>
                  <td style={{ padding: '0.875rem 1rem', textAlign: 'left' }}>G&A Rate</td>
                  <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', color: 'var(--success-green)' }}>
                    {formatValue(gaComponents.rate, 'rate')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Division OH Breakdown for G&A */}
        {gaComponents.division_oh_breakdown && gaComponents.division_oh_breakdown.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Division OH Breakdown (included in G&A Base)
            </h4>
            <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
              <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
                <thead>
                  <tr style={{ background: '#1a1a1a' }}>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Division</th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>OH Allocation</th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Base</th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {gaComponents.division_oh_breakdown.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#FAFAFA' }}>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '500' }}>{row.label}</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                        {formatValue(row.oh_allocation)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                        {formatValue(row.base)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', color: 'var(--success-green)' }}>
                        {formatValue(row.rate, 'rate')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Legacy Schedule E format (old fringe benefits detail)
  if (section.format === 'schedule_e') {
    const summary = section.summary || {};
    const detailRows = section.detail_rows || [];
    
    return (
      <div>
        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div className="pro-stat-card">
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Total Fringe Base
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-blue)' }}>
              {formatValue(summary.total_fringe_base)}
            </div>
          </div>
          <div className="pro-stat-card">
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Labor Base
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-blue)' }}>
              {formatValue(summary.fringe_base)}
            </div>
          </div>
          <div className="pro-stat-card">
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Fringe Rate
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-green)' }}>
              {formatValue(summary.fringe_rate, 'rate')}
            </div>
          </div>
          <div className="pro-stat-card">
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Account Count
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--accent-orange)' }}>
              {summary.account_count || 0}
            </div>
          </div>
        </div>
        
        {/* Detailed Account Table */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Fringe Benefits by GL Account
          </h4>
          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
            <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
              <thead>
                <tr style={{ background: '#1a1a1a' }}>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Account Code</th>
                  <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: '#ffffff' }}>Expenses per G/L</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#FAFAFA' }}>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>{row.account_code}</td>
                    <td style={{ 
                      padding: '0.75rem 1rem', 
                      textAlign: 'right', 
                      fontFamily: 'Inter, monospace', 
                      fontVariantNumeric: 'tabular-nums' 
                    }}>
                      {formatValue(row.amount)}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr style={{ background: '#FFF7ED', fontWeight: '700' }}>
                  <td style={{ padding: '0.875rem 1rem', textAlign: 'left' }}>Total Fringe Base</td>
                  <td style={{ 
                    padding: '0.875rem 1rem', 
                    textAlign: 'right', 
                    fontFamily: 'Inter, monospace', 
                    fontVariantNumeric: 'tabular-nums' 
                  }}>
                    {formatValue(summary.total_fringe_base)}
                  </td>
                </tr>
                {/* Rate Row */}
                <tr style={{ background: '#E8F5E9', fontWeight: '600' }}>
                  <td style={{ padding: '0.875rem 1rem', textAlign: 'left' }}>Fringe Rate</td>
                  <td style={{ 
                    padding: '0.875rem 1rem', 
                    textAlign: 'right', 
                    fontFamily: 'Inter, monospace', 
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--success-green)'
                  }}>
                    {formatValue(summary.fringe_rate, 'rate')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
  
  if (section.rows) {
    // Simple structure with rows only (all sections now use this)
    return (
      <div>
        {section.key === 'alloc_facility' && section.totals && (
          <div style={{ marginBottom: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            {/* Always show Total Pool — pool_total can be non-zero even when sqft_data is absent (M2/M5 fix) */}
            <div style={{ padding: '0.75rem 1rem', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '0.75rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>Total Pool $</div>
              <div style={{ fontWeight: 600 }}>{formatValue(section.totals.pool != null ? section.totals.pool : 0)}</div>
            </div>
            {section.totals.total_amount != null && section.totals.total_amount !== 0 ? (
              <div style={{ padding: '0.75rem 1rem', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>SQFT Amount Total</div>
                <div style={{ fontWeight: 600 }}>{formatValue(section.totals.total_amount)}</div>
              </div>
            ) : null}
            {section.totals.total_sqft != null && section.totals.total_sqft !== 0 ? (
              <div style={{ padding: '0.75rem 1rem', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>Square Footage Total</div>
                <div style={{ fontWeight: 600 }}>{formatValue(section.totals.total_sqft)}</div>
              </div>
            ) : null}
            <div style={{ padding: '0.75rem 1rem', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '0.75rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.25rem' }}>Allocation Basis</div>
              <div style={{ fontWeight: 600 }}>{section.totals.uses_explicit_percent ? 'Workbook Percent Column' : 'Derived from Basis Total'}</div>
            </div>
          </div>
        )}
        {sectionKey.startsWith('fringe') && (() => {
          // Prefer base_components (labeled per-rule breakdown from backend)
          // Fall back to filtering section.rows for non-total/non-rate/non-pool rows
          const baseComponents = section.base_components || [];
          const rows = section.rows || [];

          const detailRows = baseComponents.length > 0
            ? baseComponents.map(c => ({
                label: c.label,
                poolCode: c.pool_code || '',
                reference: c.reference || '',
                value: c.amount,
              }))
            : rows.filter(r => {
                const lbl = (r.label || r.name || '').toLowerCase();
                return !lbl.includes('total') && !lbl.includes('rate') && !lbl.includes('pool');
              });
          const hasPoolCodes = detailRows.some(r => r.poolCode);
          const hasReference = detailRows.some(r => r.reference);

          // Total labor base = sum of all labor category amounts
          const totalLaborBase = detailRows.reduce((sum, r) => sum + (parseFloat(r.value !== undefined ? r.value : r.amount) || 0), 0);

          // Total pool from totals object (set by backend for fringe sections) or rows
          const poolRow = rows.find(r => {
            const lbl = (r.label || r.name || '').toLowerCase();
            return lbl === 'pool' || lbl === 'total pool';
          });
          const totalPool = (parseFloat(section.totals?.total_pool || section.totals?.pool) || 0)
            || (poolRow ? (parseFloat(poolRow.value !== undefined ? poolRow.value : poolRow.amount) || 0) : 0);

          if (detailRows.length === 0) return null;

          return (
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Fringe Pool Rollup
              </h4>
              <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)' }}>
                <table className="financial-table" style={{ fontSize: '0.875rem', margin: 0 }}>
                  <thead>
                    <tr style={{ background: '#E2EFDA' }}>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: 'var(--gray-800)' }}>Labor Category</th>
                      {hasPoolCodes && <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: 'var(--gray-800)' }}>Account</th>}
                      {hasReference && <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: '600', color: 'var(--gray-800)' }}>Reference</th>}
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: 'var(--gray-800)' }}>Amount</th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: 'var(--gray-800)' }}>% of Total</th>
                      <th style={{ padding: '0.875rem 1rem', textAlign: 'right', fontWeight: '600', color: 'var(--gray-800)' }}>Allocation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row, i) => {
                      const laborAmt = parseFloat(row.value !== undefined ? row.value : row.amount) || 0;
                      const pct = totalLaborBase !== 0 ? (laborAmt / totalLaborBase) * 100 : 0;
                      const allocation = (pct / 100) * totalPool;
                      return (
                        <tr
                          key={i}
                          style={{ background: i % 2 === 0 ? 'transparent' : '#FAFAFA' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100)'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#FAFAFA'}
                        >
                          <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>{row.poolCode || row.label || row.name}</td>
                          {hasPoolCodes && <td style={{ padding: '0.75rem 1rem', fontFamily: 'Inter, monospace', fontSize: '0.8rem', color: 'var(--gray-500)' }}>{row.poolCode ? (row.label || '') : ''}</td>}
                          {hasReference && <td style={{ padding: '0.75rem 1rem', fontFamily: 'Inter, monospace', fontSize: '0.8rem', color: 'var(--gray-600)' }}>{row.reference || ''}</td>}
                          <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                            {formatValue(laborAmt)}
                          </td>
                          <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums' }}>
                            {pct.toFixed(2)}%
                          </td>
                          <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', color: 'var(--success-green)', fontWeight: '600' }}>
                            {formatValue(allocation)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ background: '#FFF2CC', fontWeight: '700' }}>
                      <td style={{ padding: '0.875rem 1rem', fontWeight: '700' }}>Total</td>
                      {hasPoolCodes && <td />}
                      {hasReference && <td />}
                      <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', fontWeight: '700' }}>
                        {formatValue(totalLaborBase)}
                      </td>
                      <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', fontWeight: '700' }}>
                        100.00%
                      </td>
                      <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'Inter, monospace', fontVariantNumeric: 'tabular-nums', fontWeight: '700' }}>
                        {formatValue(totalPool)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {renderTable(section.rows)}
        {section.account_table && section.account_table.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '1rem' }}>
              Account Details (GL Accounts Rolling into Pool)
            </h4>
            {renderTable(section.account_table, 'Account Details')}
          </div>
        )}
      </div>
    );
  } else if (section.summary_rows || section.allocation_rows) {
    // Complex structure with summary and allocation rows (legacy support)
    const hcOpts = section.headcount_base ? { integerLabels: new Set(['Base', 'Total Base', 'Amount']) } : {};
    return (
      <div>
        {section.summary_rows && section.summary_rows.length > 0 && renderTable(section.summary_rows, 'Summary', hcOpts)}
        {section.allocation_rows && section.allocation_rows.length > 0 && renderTable(
          section.allocation_rows,
          section.allocation_basis ? `Allocations — ${section.allocation_basis}` : 'Allocations',
          hcOpts
        )}
        {section.account_table && section.account_table.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '1rem' }}>
              Account Details (GL Accounts Rolling into Pool)
            </h4>
            {renderTable(section.account_table, 'Account Details')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
      <Calculator style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.3 }} />
      <p>No data available for this section</p>
      <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Run calculations to see results</p>
    </div>
  );
};

const GLCalculations = ({ glId }) => {
  const { clientId } = useClient();
  const [sections, setSections] = useState([]);
  // Phase 7: start with no active section selected — the API
  // response sets it via ``setActive(filteredSections[0].key)`` after
  // the sections list loads. Prevents an initial render asking for
  // a literal ``fringe_100`` for wizard clients whose first fringe
  // section is named differently.
  const [active, setActive] = useState('');
  const [data, setData] = useState({});
  const [lastRun, setLastRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [calcProgress, setCalcProgress] = useState('');
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [showScheduleEDetail, setShowScheduleEDetail] = useState(false);
  // NOTE: the adequacy review lives on the Client Closeout tab
  // (components/ClientCloseoutPage.js), not here — it is a closeout step run
  // against the exported workbook, not part of running calculations.

  // Auth helper
  const getAuthHeaders = () => {
    const token = getStoredToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const downloadFromApi = async (url, fallbackFilename) => {
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}${body ? ` - ${body.slice(0, 200)}` : ''}`);
    }

    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') || '';
    const match = disposition.match(/filename\\*?=(?:UTF-8''|\"?)([^\";]+)/i);
    const filename = decodeURIComponent((match?.[1] || fallbackFilename).replace(/\"/g, '').trim());

    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename || fallbackFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  };

  // Full A-K export runs as a background job: the workbook is large enough for
  // a big client (e.g. ECS) to exceed Azure's ~230s request timeout and 504 if
  // built inline. Kick it off, poll, then download when it's ready.
  const runFullExport = async () => {
    if (!lastRun) return;
    setExporting(true);
    setCalcProgress('Building full export…');
    try {
      const startRes = await fetch(
        `${API}/gl-calcs/export/full-report/${encodeURIComponent(lastRun)}/start`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      if (!startRes.ok) throw new Error(`HTTP ${startRes.status}`);
      const { job_id: jobId } = await startRes.json();
      if (!jobId) throw new Error('No job_id returned');

      const maxAttempts = 400; // ~10 min at 1.5s
      for (let attempts = 1; attempts <= maxAttempts; attempts++) {
        await new Promise((r) => setTimeout(r, 1500));
        const st = await fetch(
          `${API}/gl-calcs/export/full-report/job/${encodeURIComponent(jobId)}`,
          { headers: getAuthHeaders() },
        );
        if (!st.ok) throw new Error(`Status check failed: HTTP ${st.status}`);
        const job = await st.json();
        if (job.status === 'completed') {
          setCalcProgress('Downloading…');
          await downloadFromApi(
            `${API}/gl-calcs/export/full-report/job/${encodeURIComponent(jobId)}/download`,
            job.filename || `full_schedules_A-K_${lastRun}.xlsx`,
          );
          return;
        }
        if (job.status === 'error') throw new Error(job.error || 'export failed');
        if (attempts % 4 === 0) setCalcProgress(`Building full export… ${Math.round(attempts * 1.5)}s`);
      }
      throw new Error('Export timed out (still building after 10 minutes)');
    } catch (e) {
      window.alert(`Full export failed: ${e.message || e}`);
    } finally {
      setExporting(false);
      setCalcProgress(null);
    }
  };

  const refreshSections = async () => {
    const query = clientId ? `?tenant_id=${encodeURIComponent(clientId)}` : '';
    const res = await fetch(`${API}/gl-calcs/sections${query}`, { headers: getAuthHeaders() });
    if (res.ok) {
      const j = await res.json();
      const filteredSections = (j.sections||[]).filter(s => s.key !== 'schedule_e');
      setSections(filteredSections);
      if (!filteredSections.some(s => s.key === active) && filteredSections.length) setActive(filteredSections[0].key);
    }
  };

  const loadSection = async (key, runIdParam) => {
    const rid = runIdParam || lastRun;
    if (!rid) {
      console.log(`[GL Calcs] Cannot load section ${key}: no run_id`);
      return;
    }
    console.log(`[GL Calcs] Loading section ${key} with run_id ${rid}`);
    
    // Schedule E uses a special endpoint
    let url;
    if (key === 'schedule_e') {
      url = `${API}/gl-calcs/schedule-e/${encodeURIComponent(rid)}`;
    } else {
      url = `${API}/gl-calcs/section/${encodeURIComponent(key)}?run_id=${encodeURIComponent(rid)}`;
    }
    
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (res.ok) {
      const j = await res.json();
      // Log what we actually received
      console.log(`[GL Calcs] Section ${key} loaded:`, {
        hasRows: !!j.section?.rows,
        rowsLength: j.section?.rows?.length || 0,
        hasSummaryRows: !!j.section?.summary_rows,
        summaryRowsLength: j.section?.summary_rows?.length || 0,
        hasAllocationRows: !!j.section?.allocation_rows,
        allocationRowsLength: j.section?.allocation_rows?.length || 0
      });
      setData(prev => ({ ...prev, [key]: j.section }));
    } else {
      console.error(`[GL Calcs] Failed to load section ${key}:`, res.status);
    }
  };
  useEffect(() => { if (active && lastRun) { loadSection(active); } }, [active, lastRun]);
  useEffect(() => { if (lastRun && (sections||[]).length) { prefetchAll(lastRun); } }, [sections]);

  const prefetchAll = async (runId) => {
    for (const s of sections) {
      await loadSection(s.key, runId);
    }
  };

  const runAll = async () => {
    if (!glId) { window.alert('Please select a GL dataset first.'); return; }
    setBusy(true);
    setCalcProgress('Starting calculation...');
    try {
      const glToUse = glId;
      const authHeaders = getAuthHeaders();
      
      console.log('[GL Calcs] Starting calculation for GL:', glToUse);
      
      // Start async job
      const res = await fetch(`${API}/gl-calcs/run?gl_id=${encodeURIComponent(glToUse)}`, { 
        method: 'POST',
        headers: authHeaders
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      
      const jobId = j.job_id;
      if (!jobId) throw new Error('No job_id returned');
      
      console.log('[GL Calcs] Job started:', jobId);
      setCalcProgress('Calculation job started...');
      
      // Poll for job completion
      let attempts = 0;
      const maxAttempts = 600; // 10 minutes max polling (1 second intervals)
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        
        const statusRes = await fetch(`${API}/gl-calcs/job/${jobId}`, { headers: authHeaders });
        if (!statusRes.ok) throw new Error(`Failed to check job status: ${statusRes.status}`);
        
        const statusData = await statusRes.json();
        
        // Update progress every 10 seconds
        if (attempts % 10 === 0) {
          const elapsed = Math.floor(attempts);
          setCalcProgress(`Processing rules... ${elapsed}s elapsed`);
          console.log(`[GL Calcs] Still ${statusData.status}... (${elapsed}s elapsed)`);
        }
        
        if (statusData.status === 'completed') {
          const runId = statusData.run_id;
          console.log('[GL Calcs] Job completed! Run ID:', runId);
          console.log('[GL Calcs] Result sections:', statusData.result?.sections);
          
          setCalcProgress('Calculation complete! Loading data...');
          setLastRun(runId);
          
          const runSections = (statusData.result?.sections || []).map((section) => ({
            key: section.key,
            label: section.label || section.key,
          })).filter(s => s.key !== 'schedule_e');
          if (runSections.length > 0) {
            setSections(runSections);
          } else {
            await refreshSections();
          }
          
          // Fetch all sections immediately
          console.log('[GL Calcs] Fetching all section data...');
          await prefetchAll(runId);
          
          if ((statusData.result?.sections||[]).length) {
            const firstSection = statusData.result.sections[0];
            if (firstSection && firstSection.key) {
              setActive(firstSection.key);
              console.log('[GL Calcs] Set active section to:', firstSection.key);
            }
          }
          
          setCalcProgress('');
          console.log('[GL Calcs] All data loaded successfully');
          break;
        } else if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'Calculation failed');
        }
        // else status is 'pending' or 'running', continue polling
        
        attempts++;
      }
      
      if (attempts >= maxAttempts) {
        throw new Error('Calculation timed out after 10 minutes. Please check the backend logs or try with a smaller dataset.');
      }
    } catch (e) {
      console.error('[GL Calcs] Run failed:', e);
      setCalcProgress('');
      window.alert(`Run failed: ${e.message||e}`);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    refreshSections();
  }, [clientId]);

  // Always scope the visible run to the currently selected GL.
  useEffect(() => {
    if (!glId) {
      setLastRun(null);
      setData({});
      return;
    }
    (async () => {
      try {
        const params = new URLSearchParams({ limit: '1', gl_id: glId });
        if (clientId) params.set('tenant_id', clientId);
        const res = await fetch(`${API}/gl-calcs/runs/recent?${params.toString()}`, { headers: getAuthHeaders() });
        if (res.ok) {
          const j = await res.json();
          const recentRun = j?.runs?.[0]?.run_id || null;
          setData({});
          setLastRun(recentRun);
          if (!recentRun) {
            setData({});
            await refreshSections();
          } else {
            const runRes = await fetch(`${API}/gl-calcs/runs/${encodeURIComponent(recentRun)}`, { headers: getAuthHeaders() });
            if (runRes.ok) {
              const runPayload = await runRes.json();
              const runSections = (runPayload?.sections || []).map((section) => ({
                key: section.key,
                label: section.label || section.key,
              }));
              if (runSections.length > 0) {
                setSections(runSections);
              } else {
                await refreshSections();
              }
            } else {
              await refreshSections();
            }
          }
          console.log('[GL Calcs] Loaded most recent run for selected GL:', { glId, recentRun });
        }
      } catch (err) {
        console.error('[GL Calcs] Failed to load recent run for selected GL:', err);
      }
    })();
  }, [glId, clientId]);

  // style helpers
  const hasAnyData = useMemo(() => Object.values(data||{}).some(s => (s?.rows||[]).length > 0), [data]);

  // Show Schedule E Detail view
  if (showScheduleEDetail && lastRun) {
    return (
      <div className="pro-card">
        <ScheduleEDetail 
          runId={lastRun} 
          onBack={() => setShowScheduleEDetail(false)} 
        />
      </div>
    );
  }

  return (
    <div className="pro-card">
      <div className="pro-card-header" style={{ background: 'var(--white)', borderBottom: '2px solid var(--gray-200)' }}>
        <h2 className="pro-card-title" style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Section Results</h2>
        <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '1rem' }}>
          <Info style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />
          Runs use the client's activated configuration. Click Run Calculations to generate results.
        </div>

        {/* Run Button + Export Options */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button 
            className="pro-btn pro-btn-primary" 
            style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600' }}
            onClick={runAll} 
            disabled={busy}
          >
            <RefreshCw style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' }} />
            {busy ? 'Running Calculations...' : 'Run Calculations'}
          </button>
          
          {/* Full A-K Export Button (background job — see runFullExport) */}
          {lastRun && hasAnyData && (
            <button
              className="pro-btn pro-btn-secondary"
              style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600', backgroundColor: '#2563EB', color: 'var(--white)', borderColor: '#2563EB', opacity: exporting ? 0.7 : 1 }}
              onClick={runFullExport}
              disabled={exporting || busy}
            >
              <Download style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' }} />
              {exporting ? 'Building Export…' : 'Full Export (A-K)'}
            </button>
          )}

          {/* Export All Rules (A-K) Button */}
          <button 
            className="pro-btn pro-btn-secondary" 
            style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600', backgroundColor: '#059669', color: 'var(--white)', borderColor: '#059669' }}
            onClick={async () => {
              try {
                await downloadFromApi(`${API}/gl-calcs/export/all-rules`, 'all_schedule_rules_A-K.xlsx');
              } catch (e) {
                window.alert(`All rules export failed: ${e.message||e}`);
              }
            }}
          >
            <FileSpreadsheet style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' }} />
            Export All Rules (A-K)
          </button>
          
          {calcProgress && (
            <span style={{ fontSize: '0.875rem', color: 'var(--secondary-blue)', fontWeight: '500' }}>
              {calcProgress}
            </span>
          )}
        </div>
      </div>

      {/* Collapsible Summary Dashboard - Schedule A Style */}
      {hasAnyData && lastRun && (() => {
        // Extract financial data from all sections
        const summaryData = [];
        let totalPool = 0;
        let totalBase = 0;
        
        // Allocation aggregators (Schedule D items) - Get DIRECTLY from allocation sections
        // NOT from breakdown rows within other sections
        const getAllocationTotalPool = (sectionKey) => {
          const sectionData = data[sectionKey];
          if (!sectionData) return 0;
          
          // Check summary_rows first (for allocation sections)
          const summaryRows = sectionData.summary_rows || [];
          for (const row of summaryRows) {
            const label = (row.label || '').toLowerCase();
            if (label.includes('total pool')) {
              return parseFloat(row.value || 0);
            }
          }
          
          // Check totals object — alloc_facility uses "pool", others use "total_pool"
          if (sectionData.totals) {
            const poolVal = sectionData.totals.pool != null ? sectionData.totals.pool : sectionData.totals.total_pool;
            if (poolVal != null && parseFloat(poolVal) !== 0) {
              return parseFloat(poolVal);
            }
          }
          
          // Fallback to rows
          const rows = sectionData.rows || [];
          for (const row of rows) {
            const label = (row.label || '').toLowerCase();
            if (label.includes('total pool')) {
              return parseFloat(row.value || 0);
            }
          }
          
          return 0;
        };
        
        // Phase 7: discover allocation sections by type instead of
        // hardcoding the legacy 5 keys (alloc_facility / alloc_it /
        // alloc_hr / alloc_biz_mgmt / alloc_div_oh). Wizard clients
        // with arbitrary allocation IDs (allocation_facility,
        // allocation_ops, …) and any number of allocation pools are
        // discovered automatically.
        const allocationSections = sectionsOfType(sections, 'allocation');
        
        sections.forEach(s => {
          const sectionData = data[s.key];
          if (!sectionData) return;
          
          // Check both rows and summary_rows
          const rows = sectionData.rows || sectionData.summary_rows || [];
          if (rows.length === 0) return;
          
          // Extract Pool, Base, Rate from rows
          // Priority: Total Pool > Pool, Total Base > Base
          // Rate is stored as a property on the Total Base row
          let pool = 0, base = 0, rate = 0;
          let sectionTotalPool = 0, sectionTotalBase = 0;
          
          rows.forEach(row => {
            const label = (row.label || row.name || '').toLowerCase();
            const value = parseFloat(row.value !== undefined ? row.value : row.amount || 0);
            
            if (label.includes('total pool')) {
              sectionTotalPool = value;
            } else if (label.includes('pool') && !label.includes('total')) {
              pool = value;
            }
            
            if (label.includes('total base')) {
              sectionTotalBase = value;
              // Rate is stored as a property on the Total Base row
              if (row.rate !== undefined) {
                rate = parseFloat(row.rate);
              }
            } else if (label.includes('base') && !label.includes('total') && !label.includes('fringe')) {
              base = value;
            }
            
            // Also check for standalone rate rows
            if (label === 'rate' || label.includes('rate %')) {
              rate = value;
            }
          });
          
          // Also check totals object for Total Pool
          if (sectionData.totals && sectionData.totals.total_pool && !sectionTotalPool) {
            sectionTotalPool = parseFloat(sectionData.totals.total_pool);
          }
          
          // Use Total Pool/Total Base if available, otherwise use regular pool/base
          const finalPool = sectionTotalPool || pool;
          const finalBase = sectionTotalBase || base;
          const isHeadcount = !!sectionData.headcount_base;

          totalPool += finalPool;
          // Don't add headcount base to dollar-denominated totalBase
          if (!isHeadcount) totalBase += finalBase;

          // Use explicit rate from data, or calculate from Total Pool / Total Base
          const calculatedRate = rate || (finalBase !== 0 ? finalPool / finalBase : 0);

          summaryData.push({
            section: s.label,
            pool: finalPool,
            base: finalBase,
            rate: calculatedRate,
            key: s.key,
            headcount_base: isHeadcount,
          });
        });
        
        // Phase 7: build allocation summary rows by iterating
        // allocation-typed sections from the API response. Labels
        // come from each section's onboarding-driven label (legacy
        // reference client emits "Allocations - Facility (D)" etc.
        // via the executor's section.label; wizard clients emit
        // their user-chosen names like "Service Center Allocation").
        const allocationData = allocationSections
          .map(s => ({
            key: s.key,
            label: s.label || s.key,
            amount: getAllocationTotalPool(s.key),
          }))
          .filter(a => a.amount !== 0);

        const totalAllocations = allocationData.reduce((sum, a) => sum + a.amount, 0);
        
        const weightedAvgRate = totalBase !== 0 ? totalPool / totalBase : 0;
        const sectionsWithData = summaryData.length;
        
        return (
          <div style={{ borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
            {/* Collapsible Header */}
            <div 
              onClick={() => setSummaryExpanded(!summaryExpanded)}
              style={{ 
                padding: '1rem 1.5rem', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                background: summaryExpanded ? 'var(--gray-50)' : 'var(--white)',
                transition: 'all 0.2s ease',
                borderBottom: summaryExpanded ? '1px solid var(--gray-200)' : 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-100)'}
              onMouseLeave={(e) => e.currentTarget.style.background = summaryExpanded ? 'var(--gray-50)' : 'var(--white)'}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.75rem', color: 'var(--accent-orange)' }} />
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', margin: 0 }}>
                  Summary of GL Calculations (Schedule A)
                </h3>
                <span style={{ 
                  marginLeft: '1rem', 
                  fontSize: '0.875rem', 
                  color: 'var(--gray-500)', 
                  fontWeight: '500' 
                }}>
                  {sectionsWithData} sections • ${totalPool.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} total pool
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)', fontWeight: '500' }}>
                  {summaryExpanded ? 'Collapse' : 'Expand'}
                </span>
                {summaryExpanded ? (
                  <ChevronUp style={{ width: '1.25rem', height: '1.25rem', color: 'var(--gray-600)' }} />
                ) : (
                  <ChevronDown style={{ width: '1.25rem', height: '1.25rem', color: 'var(--gray-600)' }} />
                )}
              </div>
            </div>
            
            {/* Collapsible Content */}
            <div style={{ 
              maxHeight: summaryExpanded ? '2000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.4s ease-in-out, opacity 0.3s ease',
              opacity: summaryExpanded ? 1 : 0
            }}>
              <div style={{ padding: '1.5rem' }}>
                {/* Key Metrics Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div className="pro-stat-card">
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                      Total Pool Amount
                    </div>
                    <div className="pro-stat-value" style={{ fontSize: '1.75rem', color: 'var(--primary-blue)' }}>
                      ${totalPool.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="pro-stat-label">Across all sections</div>
                  </div>
                  
                  <div className="pro-stat-card">
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                      Total Base Amount
                    </div>
                    <div className="pro-stat-value" style={{ fontSize: '1.75rem', color: 'var(--primary-blue)' }}>
                      ${totalBase.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="pro-stat-label">Allocation base</div>
                  </div>
                  
                  <div className="pro-stat-card">
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                      Weighted Avg Rate
                    </div>
                    <div className="pro-stat-value" style={{ fontSize: '1.75rem', color: 'var(--success-green)' }}>
                      {(weightedAvgRate * 100).toFixed(2)}%
                    </div>
                    <div className="pro-stat-label">Overall indirect rate</div>
                  </div>
                  
                  <div className="pro-stat-card">
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                      Sections Calculated
                    </div>
                    <div className="pro-stat-value" style={{ fontSize: '1.75rem', color: 'var(--accent-orange)' }}>
                      {sectionsWithData}/{sections.length}
                    </div>
                    <div className="pro-stat-label">Processing complete</div>
                  </div>
                </div>
                
                {/* Summary Table */}
                {summaryData.length > 0 && (
                  <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--gray-200)' }}>
                    <table className="financial-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '1rem' }}>Section / Cost Type</th>
                          <th style={{ textAlign: 'right', padding: '1rem' }}>Pool Amount</th>
                          <th style={{ textAlign: 'right', padding: '1rem' }}>Base Amount</th>
                          <th style={{ textAlign: 'right', padding: '1rem' }}>Rate</th>
                          <th style={{ textAlign: 'right', padding: '1rem' }}>% of Total Pool</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.map((item, idx) => (
                          <tr 
                            key={idx}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setActive(item.key)}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <td style={{ padding: '0.875rem 1rem', fontWeight: '500' }}>{item.section}</td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>
                              ${item.pool.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>
                              {item.headcount_base
                                ? Math.round(item.base).toLocaleString()
                                : `$${item.base.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>
                              {item.headcount_base
                                ? `$${item.rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/HC`
                                : `${(item.rate * 100).toFixed(2)}%`}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>
                              {totalPool !== 0 ? ((item.pool / totalPool) * 100).toFixed(2) : '0.00'}%
                            </td>
                          </tr>
                        ))}
                        {/* Total Row */}
                        <tr className="table-row-summary">
                          <td style={{ padding: '1rem', fontWeight: '700', fontSize: '0.9375rem' }}>TOTAL</td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: '700' }}>
                            ${totalPool.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: '700' }}>
                            ${totalBase.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: '700' }}>
                            {(weightedAvgRate * 100).toFixed(2)}%
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: '700' }}>
                            100.00%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Allocations Summary Table (Schedule D) */}
                {allocationData.length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Intermediate Allocations (Schedule D)
                    </h4>
                    <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--gray-200)' }}>
                      <table className="financial-table" style={{ margin: 0 }}>
                        <thead>
                          <tr style={{ background: '#E2EFDA' }}>
                            <th style={{ textAlign: 'left', padding: '0.875rem 1rem', color: 'var(--gray-800)' }}>Allocation Type</th>
                            <th style={{ textAlign: 'right', padding: '0.875rem 1rem', color: 'var(--gray-800)' }}>Total Amount</th>
                            <th style={{ textAlign: 'right', padding: '0.875rem 1rem', color: 'var(--gray-800)' }}>% of Total Allocations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allocationData.map((alloc, idx) => (
                            <tr 
                              key={idx}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>{alloc.label}</td>
                              <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>
                                ${alloc.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace' }}>
                                {totalAllocations !== 0 ? ((alloc.amount / totalAllocations) * 100).toFixed(2) : '0.00'}%
                              </td>
                            </tr>
                          ))}
                          {/* Total Row */}
                          <tr style={{ background: '#FFF2CC', fontWeight: '700' }}>
                            <td style={{ padding: '0.875rem 1rem', fontWeight: '700' }}>TOTAL ALLOCATIONS</td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: '700' }}>
                              ${totalAllocations.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td style={{ padding: '0.875rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: '700' }}>
                              100.00%
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--white)', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-200)', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                  <strong>Run ID:</strong> {lastRun} • <strong>Generated:</strong> {new Date().toLocaleDateString()} • <strong>Status:</strong> <span style={{ color: 'var(--success-green)', fontWeight: '600' }}>Complete</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      
      <div className="pro-card-content" style={{ padding: 0 }}>
        {/* Dropdown Section Selector + Content */}
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', minHeight: '600px' }}>
          {/* Left Sidebar Navigation */}
          <div style={{ borderRight: '2px solid var(--gray-200)', background: 'var(--white)' }}>
            <h3 className="section-label-header">
              CALCULATION SECTIONS
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1.5rem' }}>
              {sections.map(s => (
                <button
                  key={s.key}
                  onClick={() => setActive(s.key)}
                  style={{
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: active === s.key ? 'var(--accent-orange)' : 'var(--white)',
                    color: active === s.key ? 'var(--white)' : 'var(--gray-700)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: active === s.key ? '0 2px 4px rgba(249, 115, 22, 0.2)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (active !== s.key) {
                      e.currentTarget.style.background = 'var(--gray-100)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (active !== s.key) {
                      e.currentTarget.style.background = 'var(--white)';
                    }
                  }}
                >
                  <span>{s.label}</span>
                  {active === s.key && <span style={{ fontSize: '1rem' }}>→</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Right Content Area */}
          <div style={{ padding: '1.5rem', background: 'var(--white)' }}>
            {sections.map(s => (
              active === s.key && (
                <div key={s.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                      {s.label}
                    </h3>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {/* Schedule E gets special buttons */}
                      {s.key === 'schedule_e' && lastRun && (
                        <>
                          <button 
                            className="pro-btn pro-btn-secondary" 
                            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                            onClick={() => setShowScheduleEDetail(true)}
                          >
                            <List style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                            View Account Details
                          </button>
                          <button 
                            className="pro-btn pro-btn-primary" 
                            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', backgroundColor: 'var(--success-green)', borderColor: 'var(--success-green)' }}
                            onClick={async () => {
                              try {
                                await downloadFromApi(
                                  `${API}/gl-calcs/export/schedule-e/${encodeURIComponent(lastRun)}`,
                                  `schedule_e_summary_${lastRun}.xlsx`
                                );
                              } catch (e) {
                                window.alert(`Export failed: ${e.message || e}`);
                              }
                            }}
                          >
                            <FileSpreadsheet style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                            Export Summary
                          </button>
                          <button 
                            className="pro-btn pro-btn-primary" 
                            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', backgroundColor: '#4472C4', borderColor: '#4472C4' }}
                            onClick={async () => {
                              try {
                                await downloadFromApi(
                                  `${API}/gl-calcs/export/schedule-e-detail/${encodeURIComponent(lastRun)}`,
                                  `schedule_e_detail_${lastRun}.xlsx`
                                );
                              } catch (e) {
                                window.alert(`Export failed: ${e.message || e}`);
                              }
                            }}
                          >
                            <FileSpreadsheet style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                            Export Detail
                          </button>
                        </>
                      )}
                      <button 
                        className="pro-btn pro-btn-secondary" 
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                        onClick={() => {
                          const sect = data[s.key];
                          // For Schedule E, use detail_rows instead
                          const rows = s.key === 'schedule_e' ? (sect?.detail_rows || []) : (sect?.rows || []);
                          if (!rows.length) { window.alert('No rows to export'); return; }
                          const cols = Object.keys(rows[0]);
                          const csv = [cols.join(',')].concat(rows.map(r => cols.map(c => JSON.stringify(r[c] ?? '')).join(','))).join('\n');
                          const blob = new window.Blob([csv], { type: 'text/csv;charset=utf-8;' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a'); 
                          a.href = url; 
                          a.download = `${s.key}.csv`; 
                          document.body.appendChild(a); 
                          a.click(); 
                          a.remove(); 
                          window.URL.revokeObjectURL(url);
                        }}
                      >
                        <Download style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                        Export CSV
                      </button>
                    </div>
                  </div>
                  <SectionTable section={data[s.key]} sectionKey={s.key} />
                </div>
              )
            ))}
          </div>
        </div>
        
        {!hasAnyData && (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
            <Calculator style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.3 }} />
            <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem' }}>No Data Available</p>
            <p style={{ fontSize: '0.875rem' }}>Click &quot;Run Calculations&quot; to generate results from the activated configuration.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GLCalculations;
